import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import CompassIcon from "../../assets/CompassIconPlain.png";
// #589: beforeSend can only keep events that are CAPTURED — a caught fetch
// failure that only toasts never reaches Sentry, so /users/confirm_account
// stayed dark. Both catches now capture (route-tagged by sentryFilters).
import { addBreadcrumb, captureError, captureMessage } from "../../config/sentry";
import "./Login.css";

// #608: the backend's exact vocabulary for a confirmed account, returned by
// both accept_invite and resend-otp (cortex-be#1199). Matched loosely so a
// copy tweak on one side cannot silently regress this to the generic toast.
const isAlreadyConfirmed = (message) =>
  /already confirmed/i.test(message || "");

require("dotenv").config();
const BASE_URL = process.env.REACT_APP_BASE_URL.replace("v1", "v2");

function ConfirmAccountPage() {
  const [email, setEmail] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [showResendCode, setShowResendCode] = useState(true);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // #608 (prod incident 2026-08-27): a caregiver who had already confirmed —
  // in another tab, or via the emailed link after finishing elsewhere — got
  // the generic "Failed to confirm" four times and a resend offer whose code
  // could never work. An already-confirmed account is good news; route to
  // sign-in and say so. Captured so the recurrence is measurable in Sentry.
  const goToLoginAlreadyConfirmed = (op) => {
    setShowResendCode(false);
    // Both halves (review finding): the breadcrumb carries the category
    // #608's confirming filter names, and the captured event makes it
    // transmit — a `category` TAG on the event keeps the same query
    // vocabulary searchable at the event level too.
    addBreadcrumb("register.confirm.already_confirmed", {
      category: "register.confirm.already_confirmed",
      level: "info",
      op,
    });
    captureMessage("register.confirm.already_confirmed", {
      level: "info",
      tags: {
        category: "register.confirm.already_confirmed",
        route: "/users/confirm_account",
        register_flow: "true",
        op,
      },
    });
    toast.success(
      "This account is already registered. You can sign in now — or use Forgot Password if you don't remember your password.",
    );
    setTimeout(() => {
      navigate(`/login`, { replace: true });
    }, 1500);
  };

  useEffect(() => {
    setEmail(searchParams.get("email") ?? "");
    setConfirmationCode(searchParams.get("code") ?? "");
  }, []);

  const handleResendCode = async (event) => {
    event.preventDefault();

    if (!email) {
      toast.info("Please enter your email address.");
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/users/resend-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
        }),
      });

      const resp = await response.json();

      if (resp.success) {
        toast.success("Please check your email for new code.");
        setShowResendCode(false);
      } else if (isAlreadyConfirmed(resp.message)) {
        // #608: nothing to resend — the account is registered. Say so as
        // the good news it is, and take the caregiver to sign-in.
        goToLoginAlreadyConfirmed("resend_code");
      } else {
        toast.info("Failed to resend code to your email.");
      }
    } catch (error) {
      captureError(error, {
        tags: { route: "/users/confirm_account", register_flow: "true" },
        extra: { op: "resend_code" },
      });
      toast.error("Unable to resend code to your email.");
    }
  };

  async function handleSubmit(event) {
    event.preventDefault();

    try {
      const response = await fetch(`${BASE_URL}/users/accept_invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
          confirmationCode: confirmationCode,
        }),
      });

      const resp = await response.json();

      if (resp.data) {
        setShowResendCode(false);
        toast.success("Account Confirmed Successfully");
        setTimeout(() => {
          navigate(`/login`, { replace: true });
        }, 1000);
      } else if (isAlreadyConfirmed(resp.message)) {
        goToLoginAlreadyConfirmed("confirm");
      } else {
        setShowResendCode(true);
        toast.info("Failed to confirm your account.");
      }
    } catch (error) {
      captureError(error, {
        tags: { route: "/users/confirm_account", register_flow: "true" },
        extra: { op: "confirm" },
      });
      setShowResendCode(true);
      toast.error("Unable to confirm your account.");
    }
  }

  return (
    <div className="App-content-center App-content-bg-custom">
      <div className="loginContainerUnique">
        <div className="loginLogoUnique">
          <img src={CompassIcon} alt="Compass Care by Gray Matters Alliance" />
          <h1 style={{ marginBottom: "24px", marginTop: "24px" }}>
            Check your email to verify <br />
            your account
          </h1>
          <p style={{ fontSize: "1.2rem" }}>
            We just emailed you a verification code. <br />
            Enter it here to continue registering your account.
          </p>
        </div>
        <div>
          <Box
            component="form"
            sx={{ "& > :not(style)": { m: 1, width: "25ch" } }}
            autoComplete="off"
            onSubmit={handleSubmit}
          >
            <TextField
              id="email"
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{ width: "100%" }}
            />
            <TextField
              id="confirmationCode"
              label="Confirmation Code"
              type="text"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              required
              style={{ width: "100%" }}
            />
            <br />
            <button
              type="submit"
              className="btn-primary loginButtonUnique"
              style={{ margin: "12px 0", width: "100%" }}
            >
              Submit
            </button>
            {!showResendCode ? null : (
              <a className="forgotLink" href="" onClick={handleResendCode}>
                Resend new code
              </a>
            )}
          </Box>
        </div>
      </div>
    </div>
  );
}

export default ConfirmAccountPage;
